// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { VoltageClass } from '../../../../config/powerFlowSymbols';
import type { PowerFlowRoute } from '../../../../utils/powerFlowRouting';

/**
 * How a conductor is drawn: weight from its voltage class, dash from what kind
 * of edge it is.
 *
 * Pure functions over a `VoltageClass`, deliberately with no JSX — the stroke
 * table is the part worth testing exhaustively, and it is consumed by
 * `SldEdgeLayer`, the legend, and (eventually) the symbol leads, so it cannot
 * live inside any one of them.
 *
 * **Colour is not here.** Conductors take `--sld-energized` from CSS — red
 * meaning *energized*, per ADR-0016 — and status adds a halo rather than a
 * different hue. Encoding any of that in a TS table would put hex values
 * outside the theme, which ADR-0007 forbids.
 */

export interface ConductorStroke {
  /** SVG `stroke-width`. */
  width: number;
  /** SVG `stroke-dasharray`, or undefined for a solid line. */
  dashArray?: string;
}

/**
 * Weight by voltage class.
 *
 * The one-line convention is that medium voltage is drawn heavier than low
 * voltage, so the intake run reads as the intake run at a glance. `dc` sits
 * between the two: it is a real bus, not a branch.
 */
const WIDTH: Record<VoltageClass, number> = {
  mv: 2.4,
  dc: 1.9,
  lv: 1.5,
  unknown: 1.2,
};

/**
 * An unmapped type is drawn *and* visibly uncertain.
 *
 * Finely dotted — 1 on, 3 off — which is deliberately nothing like the 5/4
 * standby dash: one means "we do not know what this is", the other means "this
 * is not normally energized", and confusing them would be a safety-relevant
 * misread rather than a cosmetic one.
 */
const UNKNOWN_DASH = '1 3';

/** A tie is dashed because its direction is not something the data settles. */
const TIE_DASH = '5 4';

/**
 * The stroke for one conductor.
 *
 * A tie keeps its own dash whatever it carries: the fact that the ranking pass
 * had to remove it outranks its voltage class as the thing a reader needs to
 * know. `straight`, `elbow` and `tap` are all the same conductor — they differ
 * only in how the router got from one end to the other — so none of them gets a
 * distinct stroke.
 */
export const conductorStroke = (
  voltageClass: VoltageClass,
  route: PowerFlowRoute = 'straight'
): ConductorStroke => {
  const width = WIDTH[voltageClass] ?? WIDTH.unknown;

  if (route === 'tie') return { width, dashArray: TIE_DASH };
  if (voltageClass === 'unknown') return { width, dashArray: UNKNOWN_DASH };
  return { width };
};

/** Stroke width alone, for callers that only size a rule (the legend). */
export const conductorWidth = (voltageClass: VoltageClass): number =>
  WIDTH[voltageClass] ?? WIDTH.unknown;
